'use client';

import { useEffect } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

/**
 * Registers the XOFFI-OS Desktop client as a pseudo push_tokens row so it
 * shows up in the push-tests UI alongside Android/iOS/Browser devices.
 *
 * Why pseudo?
 * Electron's bundled Chromium has no working FCM/GCM push service —
 * PushManager.subscribe() fails with "push service not available". So we
 * never get a real FCM token here. Instead we register a marker token
 * (`xoffi-os:<device_id>`) that the backend recognises and SKIPS for FCM
 * delivery (see push_notification_service._is_xoffi_os_pseudo_token).
 * Actual push delivery to this device happens via the SSE bridge
 * (/api/notifications/stream → notification-provider.jsx → DOM
 * `xoffi-desktop-push` event → Electron preload → main → notify-send).
 *
 * The hook only registers if the page is loaded inside the XOFFI-OS
 * Electron app (detected via the user-agent marker injected by the
 * Electron build) — regular browser sessions skip this entirely.
 */
export function useXoffiOsRegistration() {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    if (!/xoffi-os-desktop\//i.test(ua)) return;

    const token = BackendAuth.getToken?.() || BackendAuth.token;
    if (!token) return;

    // v0.6.7: prefer the stable host/user pair exposed by preload.js — that
    // produces ONE row per (machine, user) regardless of how often the Tray
    // is restarted or the Casper overlay loses localStorage. Falls back to a
    // random localStorage id for backwards-compat when the page is loaded in
    // a non-Electron browser (where window.xoffi.device is undefined).
    const dev = (typeof window !== 'undefined' && window.xoffi && window.xoffi.device) || null;
    let deviceId;
    let deviceName;
    if (dev && dev.host && dev.user) {
      deviceId = `xoffi-os:${dev.host}:${dev.user}`;
      deviceName = `XOFFI-OS · ${dev.host}`;
    } else {
      deviceId = localStorage.getItem('xoffi-os-device-id');
      if (!deviceId) {
        deviceId = 'xoffi-os-' + Math.random().toString(36).slice(2, 12);
        localStorage.setItem('xoffi-os-device-id', deviceId);
      }
      deviceName = 'XOFFI-OS Desktop';
    }
    const pseudoToken = deviceId.startsWith('xoffi-os:') ? deviceId : `xoffi-os:${deviceId}`;

    if (localStorage.getItem('xoffi-os-pseudo-registered') === pseudoToken) return;

    const versionMatch = ua.match(/xoffi-os-desktop\/([\d.]+)/i);
    const appVersion = versionMatch ? versionMatch[1] : '0.6.0';

    BackendAuth.apiCallJson('/api/mobile/push/register', {
      method: 'POST',
      body: JSON.stringify({
        device_id: pseudoToken, // same as fcm_token — stable per machine
        fcm_token: pseudoToken,
        platform: 'web',
        device_name: deviceName,
        device_model: 'XOFFI-OS Desktop',
        app_version: appVersion,
      }),
    }).then(() => {
      localStorage.setItem('xoffi-os-pseudo-registered', pseudoToken);
      console.log('[XoffiOs] Pseudo-token registered for SSE-bridge:', pseudoToken);
    }).catch((err) => {
      console.warn('[XoffiOs] register failed:', err?.message);
    });
  }, []);
}
